import moment = require("moment");
import React = require("react");
import {Loader} from "../../../components/Utils/loader/Loader";
import {BarcaImageSection} from "./BarcaImageSection";
import "./EquipmentImages.scss";
import {EquipmentImageSection} from "./EquipmentImageSection";
import {ThemeProvider} from "@vebox/etui-system";
import {veboxDefaultTheme} from "../../../index";
import {VeboxModels} from "../../../types/veboxModels";
import {VeBoxModal} from "../../../components/modal/VeBoxModal";
import {Display} from "../../../components/Display";
import {AppTranslations} from "../../../components/Utils/translations/AppTranslations";
import {EquipmentImagesController} from "./EquipementImageController";
import {Button} from "@vebox/etui-core";

declare var AppTranslations: AppTranslations;

const ADD_EQUIPMENT_PHOTO_PERMISSIONS = ["ADD_EQUIPMENT_PHOTO", "ADD_EQUIPMENT_PHOTO_FROM_GALLERY"];
const MAX_ALLOWED_DIMENSION_BYTES = 10 * 1024 * 1024;

export interface JSONEquipmentImagesConfiguration {
    equipmentGuid: string;
    barcaImagesTitle: string;
    plantImagesTitle: string;
    noImagesAvailable: string;
    noImagesAvailableSub: string;
}

interface EquipmentImagesProps {
    equipmentId: number;
}

interface EquipmentImagesState {
    configuration: JSONEquipmentImagesConfiguration;
    barcaImages: BarcaImage[];
    plantImages: EquipmentImage[];
    isVisible: boolean;
    canEditPictures: boolean;
    currentUser: VeboxModels.User;
    showModalUploadPlantImage: boolean;
    showModalDeletePlantImage: boolean;
    photoToDelete: string;
    errorUploadMessage: string;
    errorDeleteMessage: string;
    defaultSelectedImage: number;
}

interface EquipmentImage {
    guid: string;
    date: string;
    link: Image;
}

interface Image {
    href: string;
}

interface BarcaImage {
    cameraCategory: string;
    cameraIndex: number;
    image: Image;
    referenceDate: string;
}

interface EquipmentImagesInterface {
    visibilityToggle: (isVisible: boolean) => void;
}

/**
 * Converts a number in bytes to the correct string size
 * @param bytes
 * @param decimals
 */
export const formatBytes = (bytes: number, decimals = 2): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["KB", "MB", "GB"];
    const dm = decimals < 0 ? 0 : decimals;
    if (bytes >= k) {
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i - 1]}`;
    }
    return `${parseFloat((bytes / k).toFixed(dm))} ${sizes[0]}`;
};

export class EquipmentImages extends React.Component<EquipmentImagesProps, EquipmentImagesState> {
    private static logEnabled: boolean = false;
    private countDown;

    private panelInterface: EquipmentImagesInterface;

    constructor(props) {
        super(props);
        this.reloadData = this.reloadData.bind(this);
        this.log = this.log.bind(this);
        this.deletePlantImage = this.deletePlantImage.bind(this);
        this.addNewPlantImage = this.addNewPlantImage.bind(this);
        this.showDeleteModal = this.showDeleteModal.bind(this);
        this.showModalUploadPlantImage = this.showModalUploadPlantImage.bind(this);
        this.closeDeleteImageModal = this.closeDeleteImageModal.bind(this);
        this.closeUploadImageModal = this.closeUploadImageModal.bind(this);
        this.retrieveConfiguration = this.retrieveConfiguration.bind(this);


        this.state = {
            configuration: null,
            barcaImages: null,
            plantImages: null,
            isVisible: false,
            canEditPictures: false,
            currentUser: null,
            showModalUploadPlantImage: false,
            showModalDeletePlantImage: false,
            photoToDelete: "",
            errorUploadMessage: "",
            errorDeleteMessage: "",
            defaultSelectedImage: 0
        };

    }

    public componentDidMount(): void {
        this.retrieveConfiguration();
        this.countDown = setInterval(() => {
            if (this.state.isVisible) {
                this.reloadData();
            }
        }, 60000);

        this.panelInterface = {
            visibilityToggle: (isVisible: boolean) => {
                this.setState({isVisible});
            },
        };

        EquipmentImagesController.getCurrentUser()
            .then((currentUser) => {
                this.setState({currentUser, canEditPictures: this.canEditPicture(currentUser)})
            })
            .catch(() => this.log("Error requesting current user"));

        (window as any).initEquipmentImagePanelInterface(this.panelInterface);
    }

    public componentWillUnmount() {
        if (this.panelInterface) {
            this.panelInterface.visibilityToggle = undefined;
        }
    }

    public componentDidUpdate(prevProps: Readonly<EquipmentImagesProps>,
                              prevState: Readonly<EquipmentImagesState>,
                              snapshot?: any): void {
        if (prevState.configuration == null && this.state.configuration != null) {
            this.reloadData();
        }
    }

    public destroy() {
        if (this.countDown) {
            clearInterval(this.countDown);
            (window as any).equipmentImagesPageVisibility = null;
        }
    }

    public render() {
        if (this.state.configuration == null || (this.state.barcaImages == null && this.state.plantImages == null) || this.state.currentUser === null) {
            return (
                <ThemeProvider theme={veboxDefaultTheme}>
                    <Loader/>
                </ThemeProvider>
            );
        }
        let plantImages = [];
        if (this.state.plantImages != null) {
            plantImages = this.state.plantImages.map(
                (image) => {
                    // tslint:disable-next-line:max-line-length
                    return {
                        guid: image.guid,
                        href: image.link.href,
                        label: moment(new Date(image.date.substring(0, (image.date.length - 6))))
                            .format("DD/MM/YYYY HH:mm")
                    };
                },
            );
        }
        let barcaImages = [];
        if (this.state.barcaImages != null) {
            barcaImages = this.state.barcaImages.map(
                (image) => {
                    return {href: image.image.href, label: "" + (image.cameraIndex + 1)};
                },
            );
        }
        const date = this.state.barcaImages != null && this.state.barcaImages.length > 0 ?
            moment(new Date(this.state.barcaImages[0].referenceDate))
                .format("DD/MM/YYYY HH:mm") : null;
        return (
            <ThemeProvider theme={veboxDefaultTheme}>
                <div className={"equipment-images-panel"}>
                    <EquipmentImageSection
                        title={this.state.configuration.plantImagesTitle}
                        images={plantImages}
                        noImagesAvailableText={this.state.configuration.noImagesAvailable}
                        editable={this.state.canEditPictures}
                        deletePlantImages={this.showDeleteModal}
                        showModalUploadPlantImage={this.showModalUploadPlantImage}
                        defaultSelectedImage={this.state.defaultSelectedImage}
                    />
                    <div className="settingsDivider"/>
                    {
                        this.state.barcaImages != null
                        && this.state.barcaImages.length > 0
                        && (<BarcaImageSection
                            title={this.state.configuration.barcaImagesTitle}
                            images={barcaImages}
                            subtitle={date}
                            noImagesAvailableText={this.state.configuration.noImagesAvailable}
                        />)
                    }
                </div>
                {this.state.showModalUploadPlantImage && (
                    <UploadEquipmentImage
                        title={AppTranslations.equipmentImagesPage.addNewImage}
                        uploadFunction={this.addNewPlantImage}
                        errorMessage={this.state.errorUploadMessage}
                        close={this.closeUploadImageModal}
                    />
                )}
                {this.state.showModalDeletePlantImage && (
                    <DeleteConfirmationModal
                        title={AppTranslations.equipmentImagesPage.deleteImage}
                        errorMessage={this.state.errorDeleteMessage}
                        description={AppTranslations.equipmentImagesPage.confirmationDeleteImage}
                        onCancel={this.closeDeleteImageModal}
                        onSubmit={this.deletePlantImage}
                    />
                )}
            </ThemeProvider>
        );
    }

    private reloadData() {

        EquipmentImagesController.retrieveBarcaImages(this.state.configuration.equipmentGuid)
            .then((result) => this.setState({barcaImages: result.list}))
            .catch(() => {
                this.log("Error retrieving barca images")
            })

        EquipmentImagesController.retrievePlantImages(this.state.configuration.equipmentGuid)
            .then((result) => this.setState({plantImages: result.list}))
            .catch(() => {
                this.log("Error retrieving plantImages images")
            })

    }

    private retrieveConfiguration(): void {
        this.log("Retrieving configuration");
        EquipmentImagesController.retrieveConfiguration(this.props.equipmentId)
            .then((response) => {
                this.log("Success retrieving configuration");
                this.log(response);
                this.setState({configuration: response});
            })
            .catch(() => {
                this.log("Error retrieving configuration")
            })
    }

    // noinspection JSMethodCanBeStatic
    private log(message: any): void {
        if (EquipmentImages.logEnabled) {
            console.log(message);
        }
    }

    private canEditPicture(user: VeboxModels.User): boolean {
        return user.rolesAndPermissions.findIndex((role) =>
            role.permissions.findIndex(
                (permission) => ADD_EQUIPMENT_PHOTO_PERMISSIONS.includes(permission.code)) > -1) > -1
    }

    private showDeleteModal(photoGuid: string) {
        this.setState({showModalDeletePlantImage: true, photoToDelete: photoGuid})
    }

    private closeDeleteImageModal() {
        this.setState({showModalDeletePlantImage: false, errorDeleteMessage: ""})
    }

    private showModalUploadPlantImage() {
        this.setState({showModalUploadPlantImage: true})
    }

    private closeUploadImageModal() {
        this.setState({showModalUploadPlantImage: false, errorUploadMessage: ""})
    }

    private deletePlantImage() {
        if (this.state.photoToDelete !== "") {
            this.setState({errorDeleteMessage: ""})
            EquipmentImagesController.removePlantImages(this.state.photoToDelete,
                this.state.configuration.equipmentGuid)
                .then((res) => {
                    this.reloadData();
                    this.setState({showModalDeletePlantImage: false, defaultSelectedImage: 0})
                })
                .catch(() => {
                    this.log("Error deleting plant image")
                    this.setState({errorDeleteMessage: AppTranslations.equipmentImagesPage.errorDeleteImage})
                })
        }
    }

    private addNewPlantImage(image: File) {
        if (image.size <= MAX_ALLOWED_DIMENSION_BYTES) {
            this.setState({errorUploadMessage: ""})
            EquipmentImagesController.uploadPlantImages(image, this.state.configuration.equipmentGuid)
                .then(response => response.json())
                .then(() => EquipmentImagesController.retrievePlantImages(this.state.configuration.equipmentGuid))
                .then(({list}) => {
                    this.setState({showModalUploadPlantImage: false, plantImages: list})
                    return list.length
                })
                .then((totalImagesCount) => this.setState({defaultSelectedImage: totalImagesCount - 1}))
                .catch((response) => {
                    if (response.errorType === 'HTTP_PAYLOAD_TOO_LARGE') {
                        this.log("Image too big")
                        this.setState({errorUploadMessage: AppTranslations.equipmentImagesPage.imageTooBigError})
                    } else {
                        this.log("Error adding plant image")
                        this.setState({errorUploadMessage: AppTranslations.equipmentImagesPage.errorAddNewImage})
                    }

                })
        } else {
            this.setState({errorUploadMessage: AppTranslations.equipmentImagesPage.imageTooBigError})
        }
    }
}

interface UploadEquipmentImageProps {
    title: string
    errorMessage: string
    uploadFunction: (image: any) => void
    close: () => void
}

export const UploadEquipmentImage: React.FC<UploadEquipmentImageProps> = ({
                                                                              uploadFunction,
                                                                              errorMessage,
                                                                              title,
                                                                              close
                                                                          }) => {

    const imageInput = React.useRef<HTMLInputElement>(null);
    const [selectedImage, setSelectedImage] = React.useState<File>();
    const [isSendingImage, setIsSendingImage] = React.useState<boolean>(false)
    const [imageTooBig, setImageTooBig] = React.useState<boolean>(false)
    const closeButtons = () => {
        setIsSendingImage(false)
        close()
    }

    const imageChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const image: File = e.target.files[0];
            setSelectedImage(image);
            setImageTooBig(image.size > MAX_ALLOWED_DIMENSION_BYTES)
        }
    };

    const removeImage = () => {
        setSelectedImage(null);
    }

    const onClickUpload = () => {
        imageInput?.current?.click?.()
    }
    const upload = () => {
        setIsSendingImage(true)
        uploadFunction(selectedImage)
    }

    React.useEffect(() => {
        if (imageInput?.current) {
            imageInput.current.onchange = imageChange
        }
    }, [imageInput?.current])

    React.useEffect(() => {
        if (errorMessage) {
            setIsSendingImage(false)
        }
    }, [errorMessage])

    return (
        <>
            <VeBoxModal.Backdrop style={{position: "absolute", top: 0, left: 0}}>
                <VeBoxModal style={{width: 400}}>
                    <VeBoxModal.Header title={title}/>
                    <VeBoxModal.Section
                        className={"edit-picture-container equipment-image-uploader"}
                    >
                        <Display isVisible={!Boolean(errorMessage) && !imageTooBig}>
                            <p>{`${AppTranslations.equipmentImagesPage.maxImageSize} ${formatBytes(
                                MAX_ALLOWED_DIMENSION_BYTES, 0)}`}</p>
                        </Display>
                        <Display isVisible={Boolean(errorMessage) || imageTooBig}>
                            <p style={{color: "red"}}>{Boolean(errorMessage)
                                ? errorMessage
                                : AppTranslations.equipmentImagesPage.imageTooBigError}</p>
                        </Display>
                        <Display isVisible={!Boolean(selectedImage)}>
                            <Button
                                className={"addImage"}
                                shape={"circle"}
                                variant={"secondary"}
                                onClick={onClickUpload}
                                styles={{root: {width: "50px", height: "50px"}}}
                            >
                                <span className="material-icons">file_upload</span>
                            </Button>
                        </Display>
                        <Display isVisible={Boolean(selectedImage)}>
                            <img
                                id={"new-user-image-preview"}
                                height={180}
                                width={180}
                                className={"preview"}
                                src={selectedImage ? URL.createObjectURL(selectedImage) : ""}
                            />
                        </Display>
                        <Display isVisible={Boolean(selectedImage) && !isSendingImage}>
                            <Button
                                className={"removeImage"}
                                onClick={removeImage}
                                variant={"strong"}
                                shape={"circle"}
                                styles={{root: {width: "50px", height: "50px"}}}
                            >
                                <span className="material-icons-outlined">delete</span>
                            </Button>
                        </Display>
                        <Display isVisible={isSendingImage}>
                            <div className={"image-loader"}>
                                <Loader relativelyCentered={true}/>
                            </div>
                        </Display>
                    </VeBoxModal.Section>
                    <Display isVisible={!isSendingImage}>
                        <VeBoxModal.StandardActions
                            onSubmit={upload}
                            canSubmit={!imageTooBig && Boolean(selectedImage)}
                            submitText={AppTranslations.equipmentImagesPage.action}
                            onCancel={closeButtons}
                        />
                    </Display>
                </VeBoxModal>
            </VeBoxModal.Backdrop>
            <input
                type={"file"}
                name={"image"}
                accept={"image/*"}
                ref={imageInput}
                className={"input"}
                style={{display: "none"}}
            />
        </>
    );
}

type DeleteConfirmationModalProps = {
    title: string,
    description: string,
    errorMessage: string,
    onSubmit: any,
    onCancel: any,
}
const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
                                                                             description,
                                                                             onSubmit,
                                                                             title,
                                                                             onCancel,
                                                                             errorMessage
                                                                         }) => {
    const [isLoading, setIsLoading] = React.useState(false);

    const submit = () => {
        setIsLoading(true);
        onSubmit()
    }

    React.useEffect(() => {
        if (errorMessage) {
            setIsLoading(false)
        }
    }, [errorMessage])

    return (
        <VeBoxModal.Backdrop style={{position: "absolute", top: 0, left: 0}}>
            <VeBoxModal>
                <VeBoxModal.Header
                    onClose={onCancel}
                    title={title}
                />
                <VeBoxModal.Section style={{minHeight: "150px", minWidth: "350px"}}>
                    <div className={"delete-image-container"}>
                        <p>{description}</p>
                        <Display isVisible={Boolean(errorMessage)}>
                            <p className={"delete-image-error"} style={{color: "red"}}>{errorMessage}</p>
                        </Display>
                        <Display isVisible={isLoading}>
                            <div className={"delete-image-loader"}>
                                <Loader relativelyCentered={true}/>
                            </div>
                        </Display>
                    </div>
                </VeBoxModal.Section>
                <VeBoxModal.StandardActions
                    onSubmit={submit}
                    submitText={AppTranslations.equipmentImagesPage.deleteImage}
                    onCancel={onCancel}
                />
            </VeBoxModal>
        </VeBoxModal.Backdrop>
    )
}
